import React, { useState, useEffect } from 'react';
import { employeeService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Clock, Edit, RefreshCw, Search, Loader2 } from 'lucide-react';

export default function ShiftsPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editDialog, setEditDialog] = useState({ open: false, employee: null });
  const [shiftForm, setShiftForm] = useState({ shift_start: '09:00', shift_end: '18:00' });
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    loadEmployees(); // eslint-disable-line react-hooks/exhaustive-deps
  }, []);

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const data = await employeeService.list();
      setEmployees(data.filter(e => e.is_active));
    } catch {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (emp) => {
    setEditDialog({ open: true, employee: emp });
    setShiftForm({ shift_start: emp.shift_start || '09:00', shift_end: emp.shift_end || '18:00' });
  };

  const handleSave = async () => {
    setSaveLoading(true);
    try {
      await employeeService.update(editDialog.employee.id, shiftForm);
      toast.success(`Shift updated for ${editDialog.employee.first_name}`);
      setEditDialog({ open: false, employee: null });
      loadEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update shift');
    } finally {
      setSaveLoading(false);
    }
  };

  const formatShift = (start, end) => `${start || '09:00'} – ${end || '18:00'}`;

  const getShiftDuration = (start, end) => {
    if (!start || !end) return '';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins <= 0) return '';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h${m > 0 ? ` ${m}m` : ''}`;
  };

  const filtered = employees.filter(e =>
    `${e.first_name} ${e.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.department?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.employee_code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group by shift for summary
  const shiftGroups = employees.reduce((acc, e) => {
    const key = formatShift(e.shift_start, e.shift_end);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6 fade-in">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Shift Management</h1>
          <p className="text-muted-foreground mt-1">View and configure employee work shift timings</p>
        </div>
        <Button variant="outline" onClick={loadEmployees}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Shift Distribution Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(shiftGroups).map(([shift, count]) => (
          <Card key={shift}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-mono">{shift}</p>
                  <p className="text-xl font-bold">{count} <span className="text-sm font-normal text-muted-foreground">employees</span></p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, department..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Employees Table */}
      <Card>
        <CardHeader>
          <CardTitle>Employee Shifts</CardTitle>
          <CardDescription>Click Edit to update an individual's shift timing</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Designation</TableHead>
                <TableHead>Shift</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No employees found
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((emp) => (
                  <TableRow key={emp.id}>
                    <TableCell className="font-medium">{emp.first_name} {emp.last_name}</TableCell>
                    <TableCell className="font-mono text-sm">{emp.employee_code}</TableCell>
                    <TableCell><Badge variant="outline">{emp.department}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{emp.designation}</TableCell>
                    <TableCell className="font-mono text-sm font-medium">
                      {formatShift(emp.shift_start, emp.shift_end)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {getShiftDuration(emp.shift_start, emp.shift_end)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => openEdit(emp)}>
                        <Edit className="w-3 h-3 mr-1" /> Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, employee: null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Shift — {editDialog.employee?.first_name} {editDialog.employee?.last_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Shift Start *</Label>
                <Input
                  type="time"
                  value={shiftForm.shift_start}
                  onChange={(e) => setShiftForm({ ...shiftForm, shift_start: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Shift End *</Label>
                <Input
                  type="time"
                  value={shiftForm.shift_end}
                  onChange={(e) => setShiftForm({ ...shiftForm, shift_end: e.target.value })}
                />
              </div>
            </div>
            {shiftForm.shift_start && shiftForm.shift_end && (
              <p className="text-sm text-muted-foreground">
                Duration: <strong>{getShiftDuration(shiftForm.shift_start, shiftForm.shift_end)}</strong>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, employee: null })}>Cancel</Button>
            <Button onClick={handleSave} disabled={saveLoading}>
              {saveLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
